import React, { useEffect, useState, useRef } from 'react';
import { BellIcon, UserIcon, LogOutIcon, SettingsIcon, ChevronDownIcon, MapPinIcon, DollarSignIcon, ShoppingCartIcon, FileTextIcon, UsersIcon, CalendarIcon, CheckCheckIcon, XIcon } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import { loadAvatarUrl, loadNotifPrefs } from '../../pages/SettingsPage';
import { AppNotification } from '../../types';
interface HeaderProps {
  title: string;
  subtitle?: string;
  onNavigate?: (page: string) => void;
}
const notifTypeConfig: Record<AppNotification['type'], {
  icon: React.ReactNode;
  color: string;
  bg: string;
}> = {
  visita_completada: {
    icon: <MapPinIcon className="w-4 h-4" data-id="element-53" data-mp="qyl766d9obm3" />,
    color: 'text-indigo-600',
    bg: 'bg-indigo-100'
  },
  nuevo_cobro: {
    icon: <DollarSignIcon className="w-4 h-4" data-id="element-54" data-mp="37z1glqqj3f9" />,
    color: 'text-emerald-600',
    bg: 'bg-emerald-100'
  },
  nueva_venta: {
    icon: <ShoppingCartIcon className="w-4 h-4" data-id="element-55" data-mp="6w6u9mjf4xqi" />,
    color: 'text-blue-600',
    bg: 'bg-blue-100'
  },
  reporte_diario: {
    icon: <FileTextIcon className="w-4 h-4" data-id="element-56" data-mp="btgpxjxei5ms" />,
    color: 'text-violet-600',
    bg: 'bg-violet-100'
  },
  alerta_equipo: {
    icon: <UsersIcon className="w-4 h-4" data-id="element-57" data-mp="gm9ry01hizv6" />,
    color: 'text-amber-600',
    bg: 'bg-amber-100'
  },
  recordatorio_visita: {
    icon: <CalendarIcon className="w-4 h-4" data-id="element-58" data-mp="3k26xtwsm5lnp" />,
    color: 'text-rose-600',
    bg: 'bg-rose-100'
  }
};
const prefToType: Record<string, AppNotification['type']> = {
  visitasCompletadas: 'visita_completada',
  nuevosCobros: 'nuevo_cobro',
  nuevasVentas: 'nueva_venta',
  reportesDiarios: 'reporte_diario',
  alertasEquipo: 'alerta_equipo',
  recordatoriosVisita: 'recordatorio_visita'
};
function timeAgo(date: Date): string {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'ahora';
  if (mins < 60) return `hace ${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `hace ${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `hace ${days}d`;
}
export function Header({
  title,
  subtitle,
  onNavigate
}: HeaderProps) {
  const {
    user,
    logout,
    notifications,
    markNotificationsRead
  } = useAuth();
  const {
    newOrderAlert,
    clearNewOrderAlert,
    orders
  } = useApp();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string>(() => loadAvatarUrl(user?.id));
  const [prefs, setPrefs] = useState(loadNotifPrefs);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const pendingOrdersCount = orders.filter(o => o.estado === 'pendiente').length;
  useEffect(() => {
    if (newOrderAlert) {
      const timer = setTimeout(() => clearNewOrderAlert(), 8000);
      return () => clearTimeout(timer);
    }
  }, [newOrderAlert, clearNewOrderAlert]);
  const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const activeTypes = new Set(Object.entries(prefs).filter(([, enabled]) => enabled).map(([key]) => prefToType[key]).filter(Boolean));
  const visibleNotifs = notifications.filter(n => activeTypes.has(n.type));
  const unreadCount = visibleNotifs.filter(n => !n.read).length;
  const handleOpenNotif = () => {
    setNotifOpen(!notifOpen);
    setDropdownOpen(false);
  };
  const handleCloseNotif = () => {
    setNotifOpen(false);
    markNotificationsRead();
  };
  const handleMarkAllRead = () => {
    markNotificationsRead();
  };
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setDropdownOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => {
    const handleAvatarUpdate = () => setAvatarUrl(loadAvatarUrl(user?.id));
    const handleNotifsUpdate = () => setPrefs(loadNotifPrefs());
    window.addEventListener('avatar-updated', handleAvatarUpdate);
    window.addEventListener('notifs-updated', handleNotifsUpdate);
    window.addEventListener('notifications-updated', () => {});
    return () => {
      window.removeEventListener('avatar-updated', handleAvatarUpdate);
      window.removeEventListener('notifs-updated', handleNotifsUpdate);
    };
  }, []);
  useEffect(() => {
    if (user) {
      setAvatarUrl(loadAvatarUrl(user.id));
    } else {
      setAvatarUrl('');
    }
  }, [user?.id]);
  return <header className="bg-white border-b border-slate-200 relative shadow-[0_1px_2px_rgba(0,0,0,0.02)] z-20" data-id="element-59" data-mp="2vplfpuaysaed">
      <div className="px-4 lg:px-8 py-3 lg:py-5" data-id="element-60" data-mp="2qopdrma6aybl">
        <div className="flex items-center justify-between" data-id="element-61" data-mp="2glk38x5sex10">
          {/* Left side: Logo + title */}
          <div className="flex items-center gap-3" data-id="element-62" data-mp="2lkmyxqzvnqwb">
            <div className="w-8 h-8 lg:w-9 lg:h-9 bg-white rounded-xl flex items-center justify-center shadow-sm border border-slate-100 flex-shrink-0 overflow-hidden p-1" data-id="element-63" data-mp="2ll50921ecvb3">
              <img src="https://cdn.magicpatterns.com/uploads/x7GeSJnrf7zHwSFqA8Zq1u/Captura_de_pantalla_2026-02-19_124313-removebg-preview.png" alt="MR Logo" className="w-full h-full object-contain" data-id="element-64" data-mp="2vaoiq6l76n5f" />
            </div>
            {/* Desktop: full company name + page title */}
            <div className="hidden lg:block" data-id="element-65" data-mp="39azffq96uvyi">
              <h1 className="text-lg font-bold text-slate-900 font-heading tracking-tight leading-tight" data-id="element-66" data-mp="3e9u4h9xr1gr2">
                Miladys Rodríguez & Asoc.
              </h1>
              <p className="text-xs text-slate-500 font-medium flex items-center gap-1.5 mt-0.5" data-id="element-67" data-mp="1eyjb4uzydrwa">
                <span className="w-1 h-1 rounded-full bg-[#2952C4]" data-id="element-68" data-mp="1jx4a6r3gy5j5" />
                {title}
                {subtitle && <span className="text-slate-300 mx-0.5" data-id="element-69" data-mp="16viepiwgyp7t">—</span>}
                {subtitle && <span className="text-slate-400" data-id="element-70" data-mp="16xlegunfbwpq">{subtitle}</span>}
              </p>
            </div>
            {/* Mobile: just page title */}
            <div className="lg:hidden" data-id="element-71" data-mp="1gt54j0fb416k">
              <h1 className="text-sm font-bold text-slate-900 font-heading tracking-tight leading-tight" data-id="element-72" data-mp="1gsdavv59sdix">
                {title}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-2 lg:gap-3" data-id="element-73" data-mp="1ptl7m8s516aw">
            {/* New order alert banner */}
            {newOrderAlert && <div className="flex items-center gap-1 bg-amber-100 border border-amber-300 rounded-xl animate-pulse shadow-sm" data-id="element-74" data-mp="24l3m225cmss5">
                <button onClick={() => onNavigate?.('pedidos')} className="flex items-center gap-2 px-2 lg:px-3 py-1.5 text-xs font-bold text-amber-800 hover:bg-amber-200 rounded-l-xl transition-colors" data-id="element-75" data-mp="2djfjan0q0otk">
                  <ShoppingCartIcon className="w-4 h-4" data-id="element-76" data-mp="2s4jmatp3l8zg" />
                  <span className="hidden sm:inline" data-id="element-77" data-mp="2x4o54qdhafsu">¡Nuevo Pedido!</span>
                  <span className="sm:hidden" data-id="element-78" data-mp="2j67xmx7skl9n">Pedido</span>
                </button>
                <button onClick={e => {
              e.stopPropagation();
              clearNewOrderAlert();
            }} className="p-1.5 text-amber-600 hover:text-amber-800 hover:bg-amber-200 rounded-r-xl transition-colors" title="Cerrar alerta" data-id="element-79" data-mp="2o512k190j3y7">
                  <XIcon className="w-3.5 h-3.5" data-id="element-80" data-mp="20ghsphabej40" />
                </button>
              </div>}

            {/* Pending orders badge */}
            {!newOrderAlert && pendingOrdersCount > 0 && <button onClick={() => onNavigate?.('pedidos')} className="flex items-center gap-1.5 px-2 lg:px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-xl text-xs font-bold text-blue-700 hover:bg-blue-100 transition-colors shadow-sm" data-id="element-81" data-mp="1qilj4tx0zw80">
                <ShoppingCartIcon className="w-3.5 h-3.5" data-id="element-82" data-mp="260d2kx2edewm" />
                <span className="hidden sm:inline" data-id="element-83" data-mp="21xoy11kpuu5g">
                  {pendingOrdersCount} pendiente
                  {pendingOrdersCount > 1 ? 's' : ''}
                </span>
                <span className="sm:hidden" data-id="element-84" data-mp="1x0q5uokm4x9w">{pendingOrdersCount}</span>
              </button>}

            {/* Bell with notification dropdown */}
            <div className="relative" ref={notifRef} data-id="element-85" data-mp="1n7rb0oyiwahu">
              <button onClick={handleOpenNotif} className={`relative p-2 lg:p-2.5 rounded-xl transition-all duration-200 ${notifOpen ? 'text-[#2952C4] bg-blue-50' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'}`} data-id="element-86" data-mp="1i9pkxk2ojawv">
                <BellIcon className="w-5 h-5" data-id="element-87" data-mp="1989necwyr3a2" />
                {unreadCount > 0 && <span className="absolute top-1 right-1 lg:top-1.5 lg:right-1.5 min-w-[16px] h-[16px] bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 ring-2 ring-white shadow-sm" data-id="element-88" data-mp="14agf9ec3nz75">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>}
              </button>

              {notifOpen && <div className="absolute right-0 top-full mt-3 w-80 bg-white rounded-2xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.1)] border border-slate-100 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-89" data-mp="3tvrrqp6axf5w">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50/50 backdrop-blur-sm" data-id="element-90" data-mp="323sp5jynne4a">
                    <div className="flex items-center gap-2" data-id="element-91" data-mp="3ayql6zx3n6zu">
                      <BellIcon className="w-4 h-4 text-slate-600" data-id="element-92" data-mp="3b01n0u6zi4ob" />
                      <span className="text-sm font-bold text-slate-900" data-id="element-93" data-mp="2efsyek1cfnpp">
                        Notificaciones
                      </span>
                      {unreadCount > 0 && <span className="px-2 py-0.5 bg-rose-100 text-rose-700 text-[10px] font-bold rounded-full border border-rose-200" data-id="element-94" data-mp="20vy9eyo9a5h5">
                          {unreadCount} nuevas
                        </span>}
                    </div>
                    <div className="flex items-center gap-1" data-id="element-95" data-mp="2ew90o2th9m2u">
                      {unreadCount > 0 && <button onClick={handleMarkAllRead} className="flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wide rounded-lg transition-colors hover:bg-blue-50" style={{
                    color: '#2952C4'
                  }} data-id="element-96" data-mp="1i6rwxte22a3s">
                          <CheckCheckIcon className="w-3 h-3" data-id="element-97" data-mp="yiiu8pevylq1" />
                          Leídas
                        </button>}
                      <button onClick={handleCloseNotif} className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg" data-id="element-98" data-mp="13frgb135n9wk">
                        <XIcon className="w-4 h-4" data-id="element-99" data-mp="qwnf9231uhle" />
                      </button>
                    </div>
                  </div>

                  <div className="max-h-80 overflow-y-auto divide-y divide-slate-50 custom-scrollbar" data-id="element-100" data-mp="hybqz6pnnvw4">
                    {visibleNotifs.length === 0 ? <div className="py-12 text-center" data-id="element-101" data-mp="d09wlpms4a5c">
                        <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3" data-id="element-102" data-mp="87yx2nvyaqdb">
                          <BellIcon className="w-6 h-6 text-slate-300" data-id="element-103" data-mp="39xgqapwt55j" />
                        </div>
                        <p className="text-sm font-medium text-slate-900" data-id="element-104" data-mp="3uh1kmwqrcb9q">
                          Sin notificaciones
                        </p>
                        <p className="text-xs text-slate-500 mt-1" data-id="element-105" data-mp="3leseej2iv425">
                          Estás al día con todo
                        </p>
                      </div> : visibleNotifs.slice(0, 15).map(notif => {
                  const cfg = notifTypeConfig[notif.type];
                  return <div key={notif.id} className={`flex items-start gap-3 px-4 py-3.5 transition-colors ${!notif.read ? 'bg-blue-50/40' : 'hover:bg-slate-50'}`} data-id="element-106" data-mp="31utds4biqqs3">
                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm ${cfg.bg} ${cfg.color}`} data-id="element-107" data-mp="3buywh8rbvtj">
                              {cfg.icon}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-108" data-mp="hbe2cwqmiizi">
                              <div className="flex items-start justify-between gap-2" data-id="element-109" data-mp="h91btv6sv0au">
                                <p className={`text-sm font-semibold ${!notif.read ? 'text-slate-900' : 'text-slate-700'}`} data-id="element-110" data-mp="m3fe8hjvq7p0">
                                  {notif.title}
                                </p>
                                {!notif.read && <span className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5 shadow-sm" style={{
                          backgroundColor: '#2952C4'
                        }} data-id="element-111" data-mp="2fmebt5yok044" />}
                              </div>
                              <p className="text-xs text-slate-500 mt-0.5 truncate leading-relaxed" data-id="element-112" data-mp="1wrpi5fgdpc49">
                                {notif.message}
                              </p>
                              <p className="text-[10px] font-medium text-slate-400 mt-1.5" data-id="element-113" data-mp="387e72cd70l7p">
                                {timeAgo(notif.timestamp)}
                              </p>
                            </div>
                          </div>;
                })}
                  </div>

                  <div className="border-t border-slate-100 p-2 bg-slate-50/50" data-id="element-114" data-mp="cddz1n67xfsk">
                    <button onClick={() => {
                  onNavigate?.('settings');
                  setNotifOpen(false);
                }} className="w-full py-2 text-xs font-bold uppercase tracking-wide rounded-xl transition-colors hover:bg-white hover:shadow-sm text-slate-500 hover:text-[#2952C4]" data-id="element-115" data-mp="hc7ala1y2wq3">
                      Gestionar preferencias
                    </button>
                  </div>
                </div>}
            </div>

            {/* User avatar dropdown */}
            <div className="relative" ref={dropdownRef} data-id="element-116" data-mp="14zoxosa9l6mq">
              <button onClick={() => {
              setDropdownOpen(!dropdownOpen);
              setNotifOpen(false);
            }} className="flex items-center gap-3 pl-2 lg:pl-4 lg:ml-1 lg:border-l lg:border-slate-200 hover:opacity-80 transition-opacity group" data-id="element-117" data-mp="194eq275ps32w">
                <div className="text-right hidden lg:block" data-id="element-118" data-mp="2kkzk50ksv69z">
                  <p className="text-sm font-bold text-slate-900 leading-tight" data-id="element-119" data-mp="2fmfxsz2ansh3">
                    {user?.nombre}
                  </p>
                  <p className="text-xs font-medium text-slate-500" data-id="element-120" data-mp="3d8lls3agw0u7">
                    {user?.rolNombre}
                  </p>
                </div>
                <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-xl flex items-center justify-center shadow-sm ring-2 ring-white overflow-hidden flex-shrink-0 group-hover:ring-blue-50 transition-all" style={{
                background: 'linear-gradient(135deg, #3B4BC8, #2D3A8C)'
              }} data-id="element-121" data-mp="33j20ys371wwp">
                  {avatarUrl ? <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" data-id="element-122" data-mp="1pb41xdugtj9v" /> : <span className="text-xs lg:text-sm font-bold text-white" data-id="element-123" data-mp="1fmcftmc35t6v">
                      {user?.nombre ? getInitials(user.nombre) : '??'}
                    </span>}
                </div>
                <ChevronDownIcon className={`hidden lg:block w-4 h-4 text-slate-400 transition-transform duration-200 ${dropdownOpen ? 'rotate-180 text-[#2952C4]' : ''}`} data-id="element-124" data-mp="15mlhtaobgw21" />
              </button>

              {dropdownOpen && <div className="absolute right-0 top-full mt-3 w-60 bg-white rounded-2xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.1)] border border-slate-100 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-125" data-mp="11lrvglqivpb9">
                  <div className="px-5 py-4 bg-slate-50/50 border-b border-slate-100" data-id="element-126" data-mp="2vz1a0rxxe10d">
                    <div className="flex items-center gap-3" data-id="element-127" data-mp="3637wrov2cuu9">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0 shadow-sm" style={{
                    background: 'linear-gradient(135deg, #3B4BC8, #2D3A8C)'
                  }} data-id="element-128" data-mp="361mypp9jitrn">
                        {avatarUrl ? <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" data-id="element-129" data-mp="3owbrldk0pdqg" /> : <span className="text-sm font-bold text-white" data-id="element-130" data-mp="3ttc0dw894ul9">
                            {user?.nombre ? getInitials(user.nombre) : '??'}
                          </span>}
                      </div>
                      <div className="min-w-0" data-id="element-131" data-mp="1zhdofmik5klo">
                        <p className="text-sm font-bold text-slate-900 truncate" data-id="element-132" data-mp="2o10tjyzmj0yp">
                          {user?.nombre}
                        </p>
                        <p className="text-xs font-medium text-slate-500" data-id="element-133" data-mp="2sxr98l7inujl">
                          {user?.rolNombre}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="p-1.5 space-y-0.5" data-id="element-134" data-mp="31tqf62b8q3va">
                    <button onClick={() => {
                  onNavigate?.('settings');
                  setDropdownOpen(false);
                }} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-slate-700 rounded-xl hover:bg-slate-50 hover:text-[#2952C4] transition-colors" data-id="element-135" data-mp="252y9lylxnu7r">
                      <UserIcon className="w-4 h-4" data-id="element-136" data-mp="3pggh73nl9yfw" />
                      Perfil
                    </button>
                    <button onClick={() => {
                  onNavigate?.('settings');
                  setDropdownOpen(false);
                }} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-slate-700 rounded-xl hover:bg-slate-50 hover:text-[#2952C4] transition-colors" data-id="element-137" data-mp="3uf9scpfeldcw">
                      <SettingsIcon className="w-4 h-4" data-id="element-138" data-mp="3qs1vbdat81yo" />
                      Configuración
                    </button>
                  </div>
                  <div className="border-t border-slate-100 p-1.5" data-id="element-139" data-mp="3lrnq030lf64c">
                    <button onClick={() => {
                  logout();
                  setDropdownOpen(false);
                }} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-rose-600 rounded-xl hover:bg-rose-50 transition-colors" data-id="element-140" data-mp="3gtcg5saasvm4">
                      <LogOutIcon className="w-4 h-4" data-id="element-141" data-mp="1635kvulletky" />
                      Cerrar Sesión
                    </button>
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </header>;
}